import { shortestCircularDelta } from '../../_pure/circularTravel.js';
import { resolveCameraFramingPose } from './cameraFraming.js';
import { resolveCameraPose } from './cameraPose.js';
import { resolveCameraAnchor, resolveCameraViewPose as resolveStationViewPose } from './cameraStation.js';
export function resolveNavigationMode(mode, cameraActive = false) {
    return cameraActive ? 'guided' : (mode ?? 'free');
}
export function validateCameraPose(pose) {
    for (const [name, value] of [['position', pose.position], ['target', pose.target]]) {
        if (value.length !== 3 || !value.every(Number.isFinite)) {
            throw new Error(`Camera ${name} must contain three finite coordinates.`);
        }
    }
    if (!Number.isFinite(pose.fov) || pose.fov <= 0 || pose.fov >= 180) {
        throw new Error('Camera fov must be finite and between 0 and 180 degrees.');
    }
    if (pose.position.every((component, index) => component === pose.target[index])) {
        throw new Error('Camera position and target must differ.');
    }
}
export function resolveCameraNavigation(frame, navigation, start = resolveCameraPose(frame.spaceRenderOptions), aspect = 1, state) {
    if (navigation.blend && navigation.view === undefined)
        throw new Error('Camera blending requires a view destination.');
    if (navigation.orbitCenter && (navigation.orbitCenter.length !== 3 || !navigation.orbitCenter.every(Number.isFinite))) {
        throw new Error('Camera orbit center must contain three finite coordinates.');
    }
    const alternatives = [navigation.target !== undefined, navigation.framing !== undefined, navigation.view !== undefined, navigation.path !== undefined];
    if (alternatives.filter(Boolean).length !== 1)
        throw new Error('Camera navigation requires exactly one destination.');
    const graph = frame.spaceRenderOptions?.camera?.graph;
    if (navigation.path !== undefined) {
        const path = graph?.paths.find((entry) => entry.pathId === navigation.path);
        if (!path)
            throw new Error(`Camera path could not be resolved: ${navigation.path}`);
        if (path.pathKind !== 'straight')
            throw new Error(`Unsupported camera path kind: ${path.pathKind}`);
        return resolveCameraNavigation(frame, { view: path.to }, start, aspect, state);
    }
    if (navigation.view !== undefined) {
        const view = graph?.views[navigation.view];
        if (!view)
            throw new Error(`Camera view could not be resolved: ${navigation.view}`);
        const destination = resolveCameraViewPose(frame, view, start, aspect, state);
        if (!navigation.blend)
            return destination;
        const source = graph?.views[navigation.blend.fromView];
        if (!source)
            throw new Error(`Camera blend source could not be resolved: ${navigation.blend.fromView}`);
        const fraction = navigation.blend.fraction;
        if (!Number.isFinite(fraction) || fraction < 0 || fraction > 1)
            throw new Error('Camera blend fraction must be between zero and one.');
        return interpolateCameraPose(resolveCameraViewPose(frame, source, start, aspect), destination, fraction, navigation.orbitCenter);
    }
    if (state)
        delete state.current;
    if (navigation.framing !== undefined)
        return resolveCameraFramingPose(navigation.framing, start, aspect);
    const target = resolveCameraAnchor(frame, navigation.target);
    if (navigation.offset.length !== 3 || !navigation.offset.every(Number.isFinite)) {
        throw new Error('Camera navigation offset must contain three finite coordinates.');
    }
    const result = {
        position: [target[0] + navigation.offset[0], target[1] + navigation.offset[1], target[2] + navigation.offset[2]],
        target, fov: navigation.fov ?? start.fov,
    };
    validateCameraPose(result);
    return result;
}
export function cameraPosesMatch(left, right) {
    return Math.abs(left.fov - right.fov) < 1e-6
        && left.position.every((value, axis) => Math.abs(value - right.position[axis]) < 1e-6)
        && left.target.every((value, axis) => Math.abs(value - right.target[axis]) < 1e-6);
}
export function resolveCameraViewPose(frame, view, start = resolveCameraPose(frame.spaceRenderOptions), aspect = 1, state) {
    const result = resolveStationViewPose(frame, view, start, aspect, state);
    validateCameraPose(result);
    return result;
}
export function validateCameraTravel(start, end) {
    validateCameraPose(start);
    validateCameraPose(end);
    const offset = start.position.map((value, axis) => value - start.target[axis]);
    const delta = end.position.map((value, axis) => value - end.target[axis] - offset[axis]);
    const squared = delta.reduce((sum, value) => sum + value * value, 0);
    const closest = squared === 0 ? 0 : Math.max(0, Math.min(1, -offset.reduce((sum, value, axis) => sum + value * delta[axis], 0) / squared));
    if (Math.hypot(...offset.map((value, axis) => value + closest * delta[axis])) < 1e-9) {
        throw new Error('Camera travel would pass its target through the camera position.');
    }
}
export function phaseControlsCamera(phase) {
    return phase.cameraNavigation !== undefined || phase.patch.spaceRenderOptions?.camera?.pose !== undefined;
}
export function easeTransitionProgress(progress, easing = 'linear') {
    const clamped = Math.max(0, Math.min(1, progress));
    return easing === 'smoothstep' ? clamped * clamped * (3 - 2 * clamped) : clamped;
}
export function interpolateCameraPose(start, end, progress, orbitCenter) {
    const mix = (a, b) => a + (b - a) * progress;
    const result = {
        position: [mix(start.position[0], end.position[0]), mix(start.position[1], end.position[1]), mix(start.position[2], end.position[2])],
        target: [mix(start.target[0], end.target[0]), mix(start.target[1], end.target[1]), mix(start.target[2], end.target[2])],
        fov: mix(start.fov, end.fov),
    };
    if (orbitCenter) {
        if (orbitCenter.length !== 3 || !orbitCenter.every(Number.isFinite)) {
            throw new Error('Camera orbit center must contain three finite coordinates.');
        }
        // A centered overview has no radial aim angle; orbiting its aim independently can spin the view.
        const hasRadialAim = [start.target, end.target].every((target) => Math.hypot(target[0] - orbitCenter[0], target[2] - orbitCenter[2]) > 1e-8);
        for (const field of hasRadialAim ? ['position', 'target'] : []) {
            const from = start[field];
            const to = end[field];
            const fromAngle = Math.atan2(from[2] - orbitCenter[2], from[0] - orbitCenter[0]);
            const toAngle = Math.atan2(to[2] - orbitCenter[2], to[0] - orbitCenter[0]);
            const delta = shortestCircularDelta(fromAngle, toAngle);
            const radius = mix(Math.hypot(from[0] - orbitCenter[0], from[2] - orbitCenter[2]), Math.hypot(to[0] - orbitCenter[0], to[2] - orbitCenter[2]));
            const angle = fromAngle + delta * progress;
            result[field] = [orbitCenter[0] + Math.cos(angle) * radius,
                mix(from[1], to[1]), orbitCenter[2] + Math.sin(angle) * radius];
        }
    }
    validateCameraPose(result);
    return result;
}
